import { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, Check, Loader2 } from 'lucide-react';

interface DownloadZipButtonProps {
  imageIds: string[];
  /** Filnamn-bas. Default: "loupe-images". Suffixas med "-N-size.zip". */
  filenameBase?: string;
  emptyLabel?: string;
}

interface ZipStatus {
  added: number;
  total: number;
  bytes: number;
  done: boolean;
  error?: string;
}

type Size = 'preview' | 'full';

const SIZE_LABEL: Record<Size, string> = {
  preview: 'Preview (1600px)',
  full: 'Original',
};

/**
 * Knapp som triggar backend att streama en ZIP med vald storlek + alla
 * imageIds. Visar live-progress via /api/zip-status/:jobId-polling under
 * generering ("Adding 234/461"), sen tar browsern över med vanlig download-progress.
 */
export function DownloadZipButton({
  imageIds,
  filenameBase = 'loupe-images',
  emptyLabel = '0 results',
}: DownloadZipButtonProps) {
  const [size, setSize] = useState<Size>('full');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState<ZipStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pollAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: globalThis.MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 2000);
    return () => clearTimeout(t);
  }, [done]);

  // Cleanup polling on unmount
  useEffect(() => () => pollAbort.current?.abort(), []);

  async function pollProgress(jobId: string) {
    const controller = new AbortController();
    pollAbort.current = controller;
    while (!controller.signal.aborted) {
      try {
        const res = await fetch(`/api/zip-status/${jobId}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) break;
        const status = (await res.json()) as ZipStatus;
        setProgress(status);
        if (status.done) break;
      } catch {
        break;
      }
      // Poll var 400ms — snabbt nog att kännas live, ej för aggressivt
      await new Promise((r) => setTimeout(r, 400));
    }
  }

  async function downloadZip(chosenSize: Size) {
    if (imageIds.length === 0) return;
    setBusy(true);
    setError(null);
    setProgress({ added: 0, total: imageIds.length, bytes: 0, done: false });
    try {
      // Kicka igång download via fetch — vi behöver headers (för Job-ID) innan
      // streamen startar. Sen läser vi blob och triggar download via <a>.
      const filename = `${filenameBase}-${imageIds.length}-${chosenSize === 'full' ? 'original' : '1600'}.zip`;
      const res = await fetch('/api/images/zip', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageIds, size: chosenSize, filename }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const jobId = res.headers.get('X-Loupe-Job-Id');
      if (jobId) {
        // Starta polling i parallell — uppdaterar `progress` medan stream pågår
        pollProgress(jobId);
      }

      // Browser strömmar response till blob — visar sin egen progressbar i tabben
      const blob = await res.blob();

      // Trigga download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      setDone(true);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte ladda ner');
    } finally {
      setBusy(false);
      pollAbort.current?.abort();
      setProgress(null);
    }
  }

  const isEmpty = imageIds.length === 0;
  const label = isEmpty
    ? emptyLabel
    : busy && progress
    ? progress.total > 0
      ? `Adding ${progress.added}/${progress.total}`
      : 'Preparing…'
    : done
    ? `Saved ${imageIds.length}!`
    : `Download ${imageIds.length}`;

  return (
    <div ref={menuRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => downloadZip(size)}
        disabled={isEmpty || busy}
        title={isEmpty ? 'Filtrera fram bilder först' : `Ladda ner ZIP (${SIZE_LABEL[size]})`}
        className={[
          'flex items-center gap-2 pl-3.5 pr-2.5 py-2 text-xs min-h-[36px] rounded-l-lg transition-colors border border-r-0',
          isEmpty
            ? 'opacity-40 cursor-not-allowed text-loupe-text-3 bg-loupe-bg-2 border-loupe-border'
            : done
            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
            : busy
            ? 'text-loupe-text bg-loupe-bg-3 border-loupe-border-2'
            : 'text-loupe-text-2 bg-loupe-bg-2 border-loupe-border hover:text-loupe-text hover:bg-loupe-bg-3',
        ].join(' ')}
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : done ? (
          <Check className="w-3.5 h-3.5" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        <span className="whitespace-nowrap tabular-nums">{label}</span>
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isEmpty || busy}
        title="Välj storlek"
        aria-label="Välj storlek"
        className={[
          'flex items-center justify-center w-8 min-h-[36px] rounded-r-lg transition-colors border',
          isEmpty
            ? 'opacity-40 cursor-not-allowed text-loupe-text-3 bg-loupe-bg-2 border-loupe-border'
            : 'text-loupe-text-2 bg-loupe-bg-2 border-loupe-border hover:text-loupe-text hover:bg-loupe-bg-3',
        ].join(' ')}
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={[
            'absolute right-0 top-full mt-1.5 z-[60]',
            'bg-loupe-bg-2 border border-loupe-border rounded-lg shadow-xl',
            'min-w-[220px] py-1 overflow-hidden',
          ].join(' ')}
        >
          <div className="px-3 py-2 text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 border-b border-loupe-border">
            Storlek
          </div>
          {(['full', 'preview'] as Size[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setSize(s);
                downloadZip(s);
              }}
              className={[
                'w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2',
                'hover:bg-loupe-bg-3',
                size === s ? 'text-loupe-text' : 'text-loupe-text-2',
              ].join(' ')}
            >
              <span>{SIZE_LABEL[s]}</span>
              {size === s ? <Check className="w-3 h-3 text-loupe-text-3" /> : null}
            </button>
          ))}
          <div className="px-3 py-2 text-[10px] text-loupe-text-3 border-t border-loupe-border">
            ZIP-fil med alla bilder.
          </div>
        </div>
      )}

      {error ? (
        <div className="absolute right-0 top-full mt-1.5 px-3 py-1.5 bg-red-500/15 border border-red-500/30 rounded-md text-[11px] text-red-300 whitespace-nowrap z-[70]">
          {error}
        </div>
      ) : null}
    </div>
  );
}
